<template>
<div>

  <v-app-bar class="px-md-4 page-title-app-bar" app :height="height" dense elevate-on-scroll v-if="pageTitleComputed">

    <div style="width: 50px;" v-if="showMenuOffset"></div> <!-- offset for the Nav-bar menu button -->

    <div class="text-center text-lg-left">
      <div class="text-center text-lg-left">
        <div class="mr-2" v-if="showBackButton" @click="goBack">
          <v-btn small outlined color="secondary" fab>
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <v-app-bar-title>{{ pageTitleComputed }}</v-app-bar-title>

    <v-spacer></v-spacer>

    <!-- prefer slot when provided, otherwise render route meta actions -->
    <div class="page-title-actions">
      <template v-if="$slots.actions">
        <slot name="actions"></slot>
      </template>

      <!-- Mobile: show actions inside a pulldown menu -->
      <template v-else-if="routeActions && routeActions.length > 1 && $vuetify.breakpoint.mobile">
        <v-menu
            v-model="actionsMenu"
            top
            offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
                color="primary"
                dark
                v-bind="attrs"
                v-on="on"
                small
                depressed
                fab
                class="ml-2"
            >
              <v-icon>mdi-dots-horizontal</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item
                v-for="(action, index) in visiblePageActions"
                :key="action.route || action.label || index"
                @click="navigate(action)"
                dense
            >
              <v-list-item-icon v-if="action.icon">
                <v-icon>{{ action.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ action.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-else-if="routeActions && routeActions.length">
        <v-btn
            v-for="action in visiblePageActions"
            :key="action.route || action.label"
            :color="action.color || 'primary'"
            :depressed="action.depressed !== false"
            class="ml-2"
            @click="navigate(action)"
        >
          <v-icon left v-if="action.icon">{{ action.icon }}</v-icon>
          {{ action.label }}
        </v-btn>
      </template>

    </div>

    <!-- named slot to render content below the title, used for tabs -->
    <div class="below-title" v-if="$slots.belowTitle">
      <slot name="belowTitle"></slot>
    </div>

  </v-app-bar>
</div>
</template>
<script>
import userHelpers from "@/mixins/userHelpers";

export default {
  name: 'PageTitle',
  mixins: [
    userHelpers,
  ],
  props: {
    pageTitle: {
      type: String,
    },
    addNavbarButtonOffset: {
      type: Boolean,
      default: false,
    },
    height: {
      type: [String, Number],
      default: 56,
    },
  },
  data() {
    return {
      actionsMenu: false,
    };
  },
  computed: {
    showMenuOffset() {
      if (this.addNavbarButtonOffset === true) {
        return true;
      }
      // On mobile the navbar button is always visible
      return this.$vuetify.breakpoint.mobile;
    },
    currentRouteTitle() {
      document.title = this.$route.meta.title;
      if (this.$route.meta.titleHidden) {
        return;
      }
      return this.$route.meta.title;
    },
    pageTitleComputed() {
      if (this.pageTitle) {
        // Set page title from property if set.
        return this.pageTitle
      }
      else if (this.currentRouteTitle) {
        // Fallback to the page title of the current route.
        return this.currentRouteTitle
      }
      else {
        return '';
      }
    },
    currentRouteRecord() {
      const matched = this.$route.matched || [];
      return matched.length ? matched[matched.length - 1] : {};
    },
    matched() {
      return this.$route.matched || [];
    },
    // If the route declares a parent by name in meta, expose it here
    backButtonRouteName() {
      return (this.currentRouteRecord && this.currentRouteRecord.meta && this.currentRouteRecord.meta.backButtonRouteName) || null;
    },
    showBackButton() {
      // Show when a meta parent is defined, or when there's a nested parent, or browser history exists
      if (this.backButtonRouteName) {
        return true;
      }
      return false;
    },
    routeActions() {
      return (this.$route && this.$route.meta && this.$route.meta.pageActions) || [];
    },
    visiblePageActions() {
      return this.routeActions.filter(action => {
        // Fallback to permission string using Vuex getter
        if (action.permission) {
          return this.userHasPermission(action.permission)
        }

        // No showIf or permission -> show by default
        return true;
      });
    }
  },
  methods: {
    goBack() {
      const parentName = this.backButtonRouteName;
      if (parentName) {
        this.$router.push({ name: parentName, params: this.$route.params }).catch(() => {});
        return;
      }
    },
    navigate(action) {
      const to = action.to || (action.route ? { name: action.route } : null);
      if (to) {
        this.$router.push(to).catch(() => {});
      }
      this.actionsMenu = false;
    },
  },
}
</script>
<style lang="scss">
// Import vuetify colors
@import '~vuetify/src/styles/styles.sass';

.v-application {
  .v-app-bar {
    background: var(--v-background-base) !important;
  }
  .theme--light {
    &.v-app-bar--is-scrolled {
      &.v-app-bar {
        background-color: rgba(255, 255, 255, 0.95) !important;
        border-bottom: 1px solid map-get($material-theme, 'dividers') !important;

        @supports (-webkit-backdrop-filter: none) or (backdrop-filter: none) {
          -webkit-backdrop-filter: saturate(180%) blur(20px);
          backdrop-filter: saturate(180%) blur(20px);
          background-color: rgba(242, 242, 246, 0.6) !important; // Same as var(--v-background-base) with some transparency
        }
      }
    }
  }
  .theme--dark {
    &.v-app-bar--is-scrolled {
      &.v-app-bar {
        background-color: rgba(0, 0, 0, 0.95) !important;
        border-bottom: 1px solid map-get($material-theme, 'dividers') !important;

        @supports (-webkit-backdrop-filter: none) or (backdrop-filter: none) {
          -webkit-backdrop-filter: saturate(180%) blur(20px);
          backdrop-filter: saturate(180%) blur(20px);
          background-color: rgba(38, 50, 56, 0.75) !important; // Same as var(--v-background-base) with some transparency
        }
      }
    }
  }
  .v-tabs {
    border-bottom: 1px solid map-get($material-theme, 'dividers') !important;
  }
}
.v-toolbar__title {
  max-width: 60%;
}
.v-app-bar-title__content {
  width: 100% !important;
}

.page-title-app-bar {

  .v-toolbar__content {
    display: flex;
    flex-flow: row wrap;
  }

  .below-title {
    flex: 1 100%;
    width: 100%;
  }
  .v-tabs > .v-tabs-bar {
    background: transparent !important;
  }
}

///* Make the app bar allow wrapping so the below-title can sit on its own row */
//.page-title-app-bar {
//  display: flex;
//  flex-wrap: wrap;
//  align-items: center;
//}
//
///* Force the below-title slot to occupy a full row and use flex layout */
//.page-title-app-bar .below-title {
//  flex-basis: 100%;
//  display: flex;
//  align-items: center;
//  margin-top: 6px;
//  padding-left: 8px; /* adjust to align with title if needed */
//}
</style>
